import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, AlertCircle, CheckCircle, Trash2 } from 'lucide-react';
import { AuthenticatedLayout } from '../layouts/AuthenticatedLayout';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { FormInput } from '../components/common/FormInput';
import { SelectInput } from '../components/common/SelectInput';
import { Loader } from '../components/common/Loader';
import { AuthContext } from '../context/AuthContext';
import { getCompanyProfile, updateCompanyProfile, deleteCompanyProfile } from '../services/company';

export const ProfilePage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const [company, setCompany] = useState(null);
  const [formData, setFormData] = useState({
    business_name: '',
    industry: '',
    address: '',
    phone: '',
    business_type: 'product',
  });

  // Get company_id from localStorage (set during login/company creation)
  const getCompanyId = () => {
    const companyId = localStorage.getItem('company_id');
    if (companyId) {
      return companyId;
    }
    
    return null;
  };

  useEffect(() => {
    const fetchCompanyProfile = async () => {
      try {
        setLoading(true);
        const companyId = getCompanyId();
        
        if (!companyId) {
          setError('Company ID not found. Please create a company profile first.');
          setLoading(false);
          return;
        }

        const data = await getCompanyProfile(companyId);
        
        setCompany(data);
        setFormData({
          business_name: data.business_name || '',
          industry: data.industry || '',
          address: data.address || '',
          phone: data.phone || '',
          business_type: data.business_type || 'product',
        });
        setError(null);
      } catch (err) {
        
        // Check if it's a 404/400 (company doesn't exist)
        if (err.response?.status === 404 || err.response?.status === 400) {
          setError('No company profile found. Please create one first.');
          setCompany(null);
        } else {
          setError(err.response?.data?.detail || err.message || 'Failed to load company profile');
        }
      } finally {
        setLoading(false);
      }
    };

    if (user?.id) {
      fetchCompanyProfile();
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSave = async () => {
    try {
      setSubmitting(true);
      setError(null);
      
      // Validate phone number is 11 digits
      if (!formData.phone || formData.phone.replace(/\D/g, '').length !== 11) {
        setError('Phone number must be 11 digits');
        setSubmitting(false);
        return;
      }
      
      const companyId = getCompanyId();
      if (!companyId) {
        setError('Company ID not found');
        return;
      }

      const updatedCompany = await updateCompanyProfile(companyId, formData);
      setCompany(updatedCompany);
      setSuccess(true);
      setIsEditing(false);
      
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to save company profile');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      setError(null);
      
      const companyId = getCompanyId();
      if (!companyId) {
        setError('Company ID not found');
        return;
      }

      await deleteCompanyProfile(companyId);
      
      // Clear auth data and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('company_id');
      
      navigate('/login');
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to delete company profile');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <AuthenticatedLayout>
        <div className="max-w-2xl mx-auto">
          <Loader fullScreen={false} message="Loading profile..." size="lg" />
        </div>
      </AuthenticatedLayout>
    );
  }

  return (
    <AuthenticatedLayout>
      <div className="w-full max-w-2xl mx-auto px-4 sm:px-6 lg:px-0">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6">Company Profile</h1>

        {error && (
          <Card className="mb-6 border border-red-200 bg-red-50">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-red-900">Error</h3>
                <p className="text-red-800 text-sm mt-1">{error}</p>
              </div>
            </div>
          </Card>
        )}

        {success && (
          <Card className="mb-6 border border-green-200 bg-green-50">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-green-900">Success</h3>
                <p className="text-green-800 text-sm mt-1">Company profile updated successfully</p>
              </div>
            </div>
          </Card>
        )}

        <Card>
          {company && (
            <>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-4 mb-6">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-blue-600 rounded-full flex flex-shrink-0 items-center justify-center text-white text-lg sm:text-2xl font-bold">
                  {company.business_name.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white break-words">{company.business_name}</h2>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">{company.industry}</p>
                </div>
              </div>

              <div className="space-y-4">
                <FormInput
                  label="Business Name"
                  type="text"
                  name="business_name"
                  value={formData.business_name}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
                
                <FormInput
                  label="Industry"
                  type="text"
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
                
                <FormInput
                  label="Address"
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
                
                <FormInput
                  label="Phone"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
                
                <SelectInput
                  label="Business Type"
                  name="business_type"
                  value={formData.business_type}
                  onChange={handleChange}
                  disabled={true}
                  options={[
                    { label: 'Product', value: 'product' },
                    { label: 'Service', value: 'service' },
                  ]}
                />

                <div className="flex gap-4 pt-4 border-t dark:border-gray-600 mt-6">
                  {!isEditing ? (
                    <>
                      <Button variant="primary" onClick={() => setIsEditing(true)}>
                        Edit Profile
                      </Button>
                      <Button 
                        variant="danger" 
                        onClick={() => setShowDeleteConfirm(true)}
                        className="flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete Account
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button 
                        variant="primary" 
                        onClick={handleSave}
                        disabled={submitting}
                      >
                        {submitting ? 'Saving...' : 'Save Changes'}
                      </Button>
                      <Button 
                        variant="secondary" 
                        onClick={() => {
                          setIsEditing(false);
                          setFormData({
                            business_name: company.business_name || '',
                            industry: company.industry || '',
                            address: company.address || '',
                            phone: company.phone || '',
                            business_type: company.business_type || 'product',
                          });
                        }}
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
          
          {!company && !loading && (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Company Profile Found</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">You haven't created a company profile yet.</p>
              <Button variant="primary" onClick={() => navigate('/company-profile')}>
                Create Company Profile
              </Button>
            </div>
          )}
        </Card>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="max-w-md">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Delete Account</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-6">
                Are you sure you want to delete your user account? This action cannot be undone.
              </p>
              <div className="flex gap-4">
                <Button 
                  variant="danger" 
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex-1"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </Button>
                <Button 
                  variant="secondary" 
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </AuthenticatedLayout>
  );
};
